import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { manpowerApi } from '@/lib/apiClient';
import RequestManpowerModal from './RequestManpowerModal';
import { isForecastRequest } from './lib/manpowerRequestForm';

/**
 * "Request manpower" on the job's Manpower tab (#303 — slice A shipped the
 * link in #377; slice 2 replaced it with the form itself; slice 4 opened the
 * same card to the estimator on a SYSTAC bid, #386).
 *
 * WHAT CHANGED, 2026-08-31 (product owner with operations): the Manpower Change Form
 * moves from Formstack into PMT and Formstack form 6095863 is retired. This
 * card used to be a deep link that opened blank and told the PM which job
 * number to type. It now opens `RequestManpowerModal`, which arrives already
 * knowing the job, the PM, the phase eligibility and whether crew is booked.
 *
 * ⚠️ ONE RULE, SERVER-SIDE. The previous version carried its own gate — PM id
 * compared to `me()`, permission checked from `me().permissions`, and the
 * workspace's `locked` prop for the lifecycle — and its own comments spent
 * forty lines explaining why each piece had to be exactly so. All of that now
 * comes from `GET /manpower/requests/new-context` as `canRequest` and
 * `requestType`, computed by the same code that enforces the POST. This card
 * cannot disagree with the API about who sees the button, because it no longer
 * has an opinion.
 *
 * ⚠️ TWO FACES, ONE CARD (decision 1 + decision 2, product owner 2026-08-31):
 *   SYSTAC bid  → the ESTIMATOR's forecast update (#386). Before slice 4 the
 *                 card hid here outright; the estimators were emailing the manpower lead
 *                 ad hoc instead. The API says who the estimator is
 *                 (`canRequest`), and `requestType` says this is a forecast.
 *   ClosedWon   → the PM's crew request. The job is won; it just has no Sage
 *                 number yet, and crew planning on a won job is real work
 *                 (#411 already lets schedulers book it).
 *   real number → the PM's crew request.
 *
 * ⚠️ Admin passes, which the oldest header explicitly refused. That refusal
 * was Formstack's — it turned admins away, so a bypass here would have led to
 * a dead end. The gate is ours now, and Admin is a super-role everywhere else;
 * see db/migrations/2026-09-02_manpower-request-permission-authorizes.sql.
 *
 * A job whose owner (PM, or estimator on a bid) is nobody you are, where you
 * hold no cover permission, shows nothing — there is nobody whose request it
 * would be.
 */
export default function ManpowerRequestCard({ projectId }: { projectId: number }) {
  const [open, setOpen] = useState(false);

  // Shared with the modal (same key), so opening the form costs no second fetch.
  const ctx = useQuery({
    queryKey: ['manpower', 'request-context', projectId],
    queryFn: () => manpowerApi.requestContext(projectId),
    enabled: Number.isFinite(projectId),
    staleTime: 30_000,
  });

  // No card while loading or on error, rather than a flash of a button that
  // may then vanish. The tab has plenty else to show.
  if (!ctx.data) return null;
  if (!ctx.data.canRequest) return null;

  const forecast = isForecastRequest(ctx.data);

  return (
    <>
      <div
        className="pw-card"
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 'var(--ew-s-4)', flexWrap: 'wrap', padding: 'var(--ew-s-4)',
          marginBottom: 'var(--ew-s-4)',
        }}
      >
        <div style={{ font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)', color: 'var(--ew-n-600)', maxWidth: '62ch' }}>
          {forecast ? (
            <>
              <strong style={{ color: 'var(--ew-dark)' }}>Has the manpower forecast for this bid changed?</strong>{' '}
              Send the schedulers an update — it is logged on the job, so the story of the pursuit is here
              instead of in email.
              {ctx.data.forecast?.peakCrew != null && (
                <> The bid currently forecasts a peak crew of {ctx.data.forecast.peakCrew}.</>
              )}
            </>
          ) : (
            <>
              <strong style={{ color: 'var(--ew-dark)' }}>Need to change the crew on this job?</strong>{' '}
              Send the schedulers a request — they review it with you and book approved crew on the
              schedule by hand.
              {ctx.data.lifecycle === 'closed-won' && (
                <> This job is won and awaiting its Sage number; crew can be planned now.</>
              )}
            </>
          )}
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setOpen(true)}
          style={{ whiteSpace: 'nowrap' }}
        >
          {forecast ? '+ Forecast Update' : '+ Manpower Request'}
        </button>
      </div>
      {/* Mounted ONLY while open (review of #439). A modal that stays mounted
          has to remember to reset every piece of state on reopen — the form,
          the success panel, AND the mutation's last error, which the first
          version forgot: a failed send's message greeted the next fresh form.
          Mounting per open makes all of it fresh by construction, and the modal
          carries no reset-on-reopen code at all as a result. */}
      {open && (
        <RequestManpowerModal projectId={projectId} isOpen onClose={() => setOpen(false)} />
      )}
    </>
  );
}
